import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Title, Text, Grid, Card, BarChart, Dialog, DialogPanel } from '@tremor/react';
import {
  ComposedChart,
  Scatter,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { X } from 'lucide-react';
import { KPICard, KPICardProps } from '@/components/KPICard';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { api, extractErrorMessage } from '@/lib/api';

interface TendenciaMes {
  mes: string;
  receita: number;
  compras: number;
  pagantes: number;
}

interface PacoteRow {
  key: string;
  nome: string;
  qtd: number;
  receita: number;
}

interface FinanceiroData {
  periodo: { days: number; inicio: string; fim: string };
  mrr: number;
  arr: number;
  receita_periodo: number;
  receita_anterior: number;
  crescimento_pct: number | null;
  compras: number;
  creditos_vendidos: number;
  orgs_total: number;
  orgs_ativas: number;
  orgs_pagantes: number;
  orgs_novas: number;
  orgs_novas_delta: number | null;
  orgs_novas_pagantes: number;
  conv_cadastro_compra: number | null;
  arpu: number | null;
  arppu: number | null;
  ltv: number | null;
  cac: number | null;
  cac_pago: number | null;
  ltv_cac: number | null;
  usd_brl: number;
  custo_ai_usd: number;
  custo_ai_brl: number;
  custo_marketing_brl: number | null;
  marketing_status: string;
  margem_bruta: number | null;
  custo_por_caso: number | null;
  casos_periodo: number;
  casos_completos: number;
  taxa_sucesso: number | null;
  churn_rate: number | null;
  churned_orgs: number;
  orgs_pagantes_anterior: number;
  receita_retida: number;
  nrr: number | null;
  tendencia_mensal: TendenciaMes[];
  pacotes: PacoteRow[];
}

// Campos que dependem do pull de ads (35-60 s a frio) — vêm de /financeiro/marketing
// com timeout longo; o /dashboard devolve-os null + 'carregando' se o cache estiver frio.
type MarketingData = Pick<
  FinanceiroData,
  'custo_marketing_brl' | 'marketing_status' | 'cac' | 'cac_pago' | 'ltv_cac' | 'custo_por_caso'
>;

type Unidade = 'brl' | 'int' | 'pct';

interface SerieData {
  disponivel: boolean;
  erro?: string;
  metrica: string;
  label: string;
  unidade: Unidade;
  days: number;
  dias: Array<{ dia: string; valor: number | null; mm7: number | null; tend: number | null }>;
  resumo: {
    media: number | null;
    min: number | null;
    max: number | null;
    dias_com_dado: number;
    tendencia_inicio: number | null;
    tendencia_fim: number | null;
    variacao_tendencia: number | null;
  };
}

const PERIODOS = [7, 30, 90, 180, 365];

const fmtBRL = (n: number | null | undefined) =>
  n === null || n === undefined
    ? '—'
    : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);

const fmtInt = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : new Intl.NumberFormat('pt-BR').format(n);

const fmtPct = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : `${(n * 100).toFixed(1)}%`;

const fmtX = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : `${n.toFixed(1)}×`;

const fmtUnidade = (u: Unidade) => (n: number | null | undefined) =>
  u === 'brl' ? fmtBRL(n) : u === 'pct' ? fmtPct(n) : fmtInt(n);

const fmtDia = (iso: string) => {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
};

// Card clicável: só os que têm série diária honesta recebem `metrica`.
function KpiClick({ metrica, onOpen, ...card }: KPICardProps & { metrica?: string; onOpen: (m: string) => void }) {
  if (!metrica) return <KPICard {...card} />;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(metrica)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onOpen(metrica);
      }}
      className="cursor-pointer rounded-tremor-default transition-shadow hover:ring-2 hover:ring-blue-500/60 focus:outline-none focus:ring-2 focus:ring-blue-500"
      title="Clique para ver a série diária"
    >
      <KPICard {...card} />
    </div>
  );
}

function SerieModal({ metrica, days, onClose }: { metrica: string | null; days: number; onClose: () => void }) {
  const open = metrica !== null;
  const { data, isLoading, error } = useQuery<SerieData>({
    queryKey: ['financeiro.serie', metrica, days],
    queryFn: async () =>
      (await api().get('/financeiro/serie', { params: { metrica, days } })).data,
    enabled: open,
  });

  const errMsg = error
    ? extractErrorMessage(error, 'erro ao carregar série')
    : data && !data.disponivel
      ? (data.erro ?? 'série indisponível')
      : null;

  const fmt = fmtUnidade(data?.unidade ?? 'int');
  const pontos = (data?.dias ?? []).map((p) => ({ ...p, label: fmtDia(p.dia) }));
  const r = data?.resumo;

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogPanel className="max-w-4xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Title>{data?.label ?? 'Série diária'}</Title>
            <Text>
              Últimos {days} dias — pontos = valor do dia · linha cheia = média móvel 7d · tracejada =
              tendência linear
            </Text>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-700 hover:text-white"
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </div>

        {isLoading && <LoadingSkeleton rows={6} />}
        {errMsg && <Text className="mt-4 text-red-400">{errMsg}</Text>}

        {data?.disponivel && (
          <>
            <div className="mt-4 h-80 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={pontos} margin={{ top: 10, right: 16, bottom: 0, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: '#94a3b8' }}
                    interval={days > 90 ? Math.floor(days / 12) : days > 30 ? 6 : 2}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#94a3b8' }}
                    tickFormatter={(v: number) =>
                      data.unidade === 'pct' ? `${Math.round(v * 100)}%` : fmtInt(Math.round(v))
                    }
                    width={64}
                  />
                  <Tooltip
                    contentStyle={{ background: '#0f172a', border: '1px solid #334155', fontSize: 12 }}
                    labelStyle={{ color: '#cbd5e1' }}
                    formatter={(v: number, name: string) => [fmt(v), name]}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Scatter name="Dia" dataKey="valor" fill="#60a5fa" />
                  <Line
                    name="Média móvel 7d"
                    type="monotone"
                    dataKey="mm7"
                    stroke="#f59e0b"
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                  />
                  <Line
                    name="Tendência"
                    type="linear"
                    dataKey="tend"
                    stroke="#34d399"
                    strokeWidth={2}
                    strokeDasharray="6 4"
                    dot={false}
                    connectNulls
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {r && (
              <Grid numItems={2} numItemsSm={5} className="mt-4 gap-3">
                <Card className="p-3">
                  <Text className="text-xs">Média/dia</Text>
                  <Text className="text-base font-semibold text-white">{fmt(r.media)}</Text>
                </Card>
                <Card className="p-3">
                  <Text className="text-xs">Mín</Text>
                  <Text className="text-base font-semibold text-white">{fmt(r.min)}</Text>
                </Card>
                <Card className="p-3">
                  <Text className="text-xs">Máx</Text>
                  <Text className="text-base font-semibold text-white">{fmt(r.max)}</Text>
                </Card>
                <Card className="p-3">
                  <Text className="text-xs">Tendência (início → fim)</Text>
                  <Text className="text-base font-semibold text-white">
                    {fmt(r.tendencia_inicio)} → {fmt(r.tendencia_fim)}
                  </Text>
                </Card>
                <Card className="p-3">
                  <Text className="text-xs">Variação da tendência</Text>
                  <Text
                    className={`text-base font-semibold ${
                      (r.variacao_tendencia ?? 0) > 0
                        ? 'text-emerald-400'
                        : (r.variacao_tendencia ?? 0) < 0
                          ? 'text-red-400'
                          : 'text-white'
                    }`}
                  >
                    {r.variacao_tendencia === null
                      ? '—'
                      : `${r.variacao_tendencia > 0 ? '+' : ''}${(r.variacao_tendencia * 100).toFixed(1)}%`}
                  </Text>
                </Card>
              </Grid>
            )}
            <Text className="mt-3 text-xs text-slate-500">
              {r?.dias_com_dado ?? 0} de {days} dias com dado. Dias sem evento contam como 0 (razões ficam
              vazias).
            </Text>
          </>
        )}
      </DialogPanel>
    </Dialog>
  );
}

export function FinanceiroPage() {
  const [days, setDays] = useState(30);
  const [serie, setSerie] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery<FinanceiroData>({
    queryKey: ['financeiro.dashboard', days],
    queryFn: async () =>
      (await api().get('/financeiro/dashboard', { params: { days } })).data,
    refetchInterval: 5 * 60_000,
  });

  // Ads é lento: query separada, timeout de 4 min (o padrão do api() é 30 s e
  // derrubava o painel inteiro). Refetch a cada 15 min = TTL do cache no back.
  const { data: mData, isLoading: mLoading, error: mError } = useQuery<MarketingData>({
    queryKey: ['financeiro.marketing', days],
    queryFn: async () =>
      (await api().get('/financeiro/marketing', { params: { days }, timeout: 240_000 })).data,
    refetchInterval: 15 * 60_000,
    retry: 1,
  });

  const errMsg = error ? extractErrorMessage(error, 'erro ao carregar financeiro') : null;
  const d = data;
  // Prefere o /marketing; enquanto ele não chega, usa o /dashboard se o cache já estava quente.
  const mk: MarketingData | undefined =
    mData ?? (d && d.marketing_status === 'ok' ? d : undefined);
  const mkLoading = isLoading || (mLoading && !mk);
  const mkStatus = mError
    ? extractErrorMessage(mError, 'erro ao puxar ads')
    : (mk?.marketing_status ?? d?.marketing_status ?? 'carregando');
  const abrir = (m: string) => setSerie(m);
  const sec = 'mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500';

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <div className="flex items-center justify-between">
          <div>
            <Title>Financeiro</Title>
            <Text>Painel consolidado — métricas SaaS · clique num card para ver a série diária</Text>
          </div>
          <div className="flex items-center gap-1 rounded-md border border-slate-700 p-0.5">
            {PERIODOS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setDays(p)}
                className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
                  days === p ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
                }`}
              >
                {p}d
              </button>
            ))}
          </div>
        </div>
      </header>

      {errMsg && (
        <Card>
          <Text className="text-red-400">{errMsg}</Text>
        </Card>
      )}

      <div>
        <Text className={sec}>Receita</Text>
        <Grid numItems={1} numItemsSm={2} numItemsLg={4} className="gap-4">
          <KpiClick metrica="mrr" onOpen={abrir} label="MRR" value={fmtBRL(d?.mrr)} hint="Receita móvel 30d" loading={isLoading} />
          <KpiClick metrica="arr" onOpen={abrir} label="ARR" value={fmtBRL(d?.arr)} hint="MRR × 12" loading={isLoading} />
          <KpiClick
            metrica="receita"
            onOpen={abrir}
            label={`Receita ${days}d`}
            value={fmtBRL(d?.receita_periodo)}
            delta={d?.crescimento_pct}
            hint={`Anterior: ${fmtBRL(d?.receita_anterior)}`}
            loading={isLoading}
          />
          <KpiClick
            metrica="compras"
            onOpen={abrir}
            label="Compras"
            value={fmtInt(d?.compras)}
            hint={`${fmtInt(d?.creditos_vendidos)} créditos vendidos`}
            loading={isLoading}
          />
        </Grid>
      </div>

      <div>
        <Text className={sec}>Crescimento</Text>
        <Grid numItems={1} numItemsSm={2} numItemsLg={4} className="gap-4">
          <KpiClick metrica="orgs_total" onOpen={abrir} label="Orgs Totais" value={fmtInt(d?.orgs_total)} loading={isLoading} />
          <KpiClick
            metrica="orgs_ativas"
            onOpen={abrir}
            label={`Orgs Ativas ${days}d`}
            value={fmtInt(d?.orgs_ativas)}
            hint={d ? `${((d.orgs_ativas / d.orgs_total) * 100).toFixed(1)}% do total` : undefined}
            loading={isLoading}
          />
          <KpiClick
            metrica="orgs_novas"
            onOpen={abrir}
            label="Novas Orgs"
            value={fmtInt(d?.orgs_novas)}
            delta={d?.orgs_novas_delta}
            loading={isLoading}
          />
          <KpiClick
            metrica="orgs_pagantes"
            onOpen={abrir}
            label="Orgs Pagantes"
            value={fmtInt(d?.orgs_pagantes)}
            hint={
              d
                ? `${fmtInt(d.orgs_novas_pagantes)} novas → conv. cadastro→compra ${fmtPct(d.conv_cadastro_compra)}`
                : undefined
            }
            loading={isLoading}
          />
        </Grid>
      </div>

      <div>
        <Text className={sec}>Unit Economics</Text>
        <Grid numItems={1} numItemsSm={2} numItemsLg={5} className="gap-4">
          <KpiClick metrica="arpu" onOpen={abrir} label="ARPU" value={fmtBRL(d?.arpu)} hint="Receita / orgs ativas" loading={isLoading} />
          <KpiClick metrica="arppu" onOpen={abrir} label="ARPPU" value={fmtBRL(d?.arppu)} hint="Receita / orgs pagantes" loading={isLoading} />
          <KpiClick onOpen={abrir} label="LTV" value={fmtBRL(d?.ltv)} hint="Lifetime (histórico)" loading={isLoading} />
          <KpiClick
            onOpen={abrir}
            label="CAC pago"
            value={fmtBRL(mk?.cac_pago)}
            hint={mk ? `Ads / novas pagantes · CAC bruto ${fmtBRL(mk.cac)}` : mkStatus}
            loading={mkLoading}
          />
          <KpiClick
            onOpen={abrir}
            label="LTV/CAC"
            value={fmtX(mk?.ltv_cac)}
            hint={
              mk?.ltv_cac && mk.ltv_cac >= 3
                ? 'Saudável (≥3×)'
                : mk?.ltv_cac
                  ? 'Abaixo do ideal (<3×)'
                  : mk && mk.custo_marketing_brl === null
                    ? 'sem gasto de ads'
                    : mkStatus
            }
            loading={mkLoading}
          />
        </Grid>
      </div>

      <div>
        <Text className={sec}>Custos & Margens</Text>
        <Grid numItems={1} numItemsSm={2} numItemsLg={4} className="gap-4">
          <KpiClick onOpen={abrir} label="Margem Bruta" value={fmtPct(d?.margem_bruta)} hint="(Receita − Custo AI em R$) / Receita" loading={isLoading} />
          <KpiClick
            metrica="custo_ai"
            onOpen={abrir}
            label="Custo AI"
            value={fmtBRL(d?.custo_ai_brl)}
            hint={d ? `US$ ${d.custo_ai_usd.toFixed(2)} × ${d.usd_brl}` : undefined}
            loading={isLoading}
          />
          <KpiClick
            onOpen={abrir}
            label="Marketing"
            value={fmtBRL(mk?.custo_marketing_brl)}
            hint={mkStatus !== 'ok' ? mkStatus : 'Meta + Google Ads'}
            loading={mkLoading}
          />
          <KpiClick
            metrica="custo_por_caso"
            onOpen={abrir}
            label="Custo/Caso"
            value={fmtBRL(mk?.custo_por_caso)}
            hint="(AI + Ads) / casos completos · série diária = só AI"
            loading={mkLoading}
          />
        </Grid>
      </div>

      <div>
        <Text className={sec}>Retenção</Text>
        <Grid numItems={1} numItemsSm={2} numItemsLg={4} className="gap-4">
          <KpiClick
            onOpen={abrir}
            label="Churn Rate"
            value={fmtPct(d?.churn_rate)}
            hint={
              d
                ? `${fmtInt(d.churned_orgs)} de ${fmtInt(d.orgs_pagantes_anterior)} pagantes do período anterior não recompraram`
                : undefined
            }
            loading={isLoading}
          />
          <KpiClick
            onOpen={abrir}
            label="NRR"
            value={fmtPct(d?.nrr)}
            hint={
              d
                ? `${fmtBRL(d.receita_retida)} de quem já pagava ÷ ${fmtBRL(d.receita_anterior)} anterior`
                : undefined
            }
            loading={isLoading}
          />
          <KpiClick
            metrica="casos_completos"
            onOpen={abrir}
            label="Casos Completos"
            value={fmtInt(d?.casos_completos)}
            hint={`de ${fmtInt(d?.casos_periodo)} total`}
            loading={isLoading}
          />
          <KpiClick metrica="taxa_sucesso" onOpen={abrir} label="Taxa de Sucesso" value={fmtPct(d?.taxa_sucesso)} loading={isLoading} />
        </Grid>
      </div>

      {d?.tendencia_mensal && d.tendencia_mensal.length > 0 && (
        <Card>
          <Title>Receita Mensal</Title>
          <Text>Últimos 12 meses — receita Stripe real</Text>
          <BarChart
            className="mt-4 h-72"
            data={d.tendencia_mensal.map((t) => ({ mes: t.mes, Receita: t.receita }))}
            index="mes"
            categories={['Receita']}
            colors={['blue']}
            valueFormatter={(n) => fmtBRL(n)}
            yAxisWidth={80}
          />
        </Card>
      )}

      {d?.pacotes && d.pacotes.length > 0 && (
        <Card>
          <Title>Vendas por Pacote</Title>
          <Text>Período selecionado</Text>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-700 text-slate-400">
                  <th className="pb-2 font-medium">Pacote</th>
                  <th className="pb-2 text-right font-medium">Qtd</th>
                  <th className="pb-2 text-right font-medium">Receita</th>
                  <th className="pb-2 text-right font-medium">% Total</th>
                </tr>
              </thead>
              <tbody>
                {d.pacotes.map((p) => (
                  <tr key={p.key} className="border-b border-slate-800">
                    <td className="py-2">{p.nome}</td>
                    <td className="py-2 text-right">{fmtInt(p.qtd)}</td>
                    <td className="py-2 text-right">{fmtBRL(p.receita)}</td>
                    <td className="py-2 text-right">
                      {d.receita_periodo > 0 ? `${((p.receita / d.receita_periodo) * 100).toFixed(1)}%` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {isLoading && !d && (
        <Card>
          <LoadingSkeleton rows={6} />
        </Card>
      )}

      <SerieModal metrica={serie} days={days} onClose={() => setSerie(null)} />
    </div>
  );
}
